import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, useMediaQuery, useTheme, Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PremiumHeader from "./components/PremiumHeader";
import SummaryCards from "./components/SummaryCards";
import FilterPanel, { defaultFilter, FilterState } from "./components/FilterPanel";
import ClassRow from "./components/ClassRow";
import AssignmentDetailsModal from "./components/AssignmentDetailsModal";
import { reportsApi, Meta } from "../shared/api";
import { useDebounced } from "../shared/useDebounced";
import { Row, Options } from "./types";
import StepUpSpinner from "../../../Components/Loader/StepUpSpinner";

function toListParams(f: FilterState, page: number, perPage: number) {
  return {
    search: f.search || undefined,
    include: f.include,
    study_mode: f.study_mode.length ? f.study_mode : undefined,
    school_grade_id: f.school_grade_id.length ? f.school_grade_id : undefined,
    subject_id: f.subject_id.length ? f.subject_id : undefined,
    staff_id: f.staff_id.length ? f.staff_id : undefined,
    day: f.day.length ? f.day : undefined,
    location_id: f.location_id.length ? f.location_id : undefined,
    assignment_status: f.assignment_status || undefined,
    class_status: f.class_status || undefined,
    start_date: f.start_date || undefined,
    end_date: f.end_date || undefined,
    page,
    per_page: perPage,
  };
}

function toSummaryParams(f: FilterState) {
  const { page: _p, per_page: _pp, ...rest } = toListParams(f, 1, 25) as any;
  return rest;
}

export default function StudentClassesReportPage() {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [filter, setFilter] = useState<FilterState>({ ...defaultFilter });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const debouncedSearch = useDebounced(filter.search, 400);
  const effectiveFilter = useMemo(
    () => ({ ...filter, search: debouncedSearch }),
    [filter, debouncedSearch]
  );

  const listParams   = useMemo(() => toListParams(effectiveFilter, page, perPage), [effectiveFilter, page, perPage]);
  const summaryKey   = useMemo(() => JSON.stringify(toSummaryParams(effectiveFilter)), [effectiveFilter]);

  const [rows, setRows] = useState<Row[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<null | "csv" | "pdf">(null);
  // Selected row for the details modal. `null` means the modal is closed.
  // One state, one modal — shared by the Class title, the "+N more"
  // affordances, and the "View Assignment Details" menu item on every row.
  const [detailsRow, setDetailsRow] = useState<Row | null>(null);

  const [options, setOptions] = useState<Options>({
    grades: [], subjects: [], staff: [], locations: [],
    study_modes: [], days: [], assignment_statuses: [],
  });

  const listReqRef = useRef(0);
  const summaryReqRef = useRef(0);
  const tableTopRef = useRef<HTMLDivElement | null>(null);

  // One-shot options load
  useEffect(() => {
    let alive = true;
    reportsApi.studentClassesOptions()
      .then((res) => { if (alive) setOptions(res); })
      .catch((e: any) => { if (alive) setError(e.message || String(e)); })
      .finally(() => { if (alive) setLoadingOptions(false); });
    return () => { alive = false; };
  }, []);

  // List loader
  const loadList = useCallback(async () => {
    const token = ++listReqRef.current;
    setLoadingList(true); setError(null);
    try {
      const res = await reportsApi.studentClasses(listParams);
      if (token !== listReqRef.current) return;
      setRows(res.data);
      setMeta(res.meta);
      setRefreshedAt(new Date());
    } catch (e: any) {
      if (token === listReqRef.current) setError(e.message || String(e));
    } finally {
      if (token === listReqRef.current) {
        setLoadingList(false);
        setFirstLoad(false);
      }
    }
  }, [listParams]);

  useEffect(() => { loadList(); }, [loadList]);

  // Summary loader — only when filters change
  useEffect(() => {
    const token = ++summaryReqRef.current;
    setLoadingSummary(true);
    reportsApi.studentClassesSummary(JSON.parse(summaryKey))
      .then((res) => {
        if (token !== summaryReqRef.current) return;
        setSummary(res as any);
      })
      .catch((e: any) => {
        if (token === summaryReqRef.current) setError(e.message || String(e));
      })
      .finally(() => {
        if (token === summaryReqRef.current) setLoadingSummary(false);
      });
  }, [summaryKey]);

  useEffect(() => { setPage(1); }, [summaryKey]);

  const doExport = async (kind: "csv" | "pdf") => {
    setExporting(kind); setError(null);
    try {
      const fn = kind === "csv" ? reportsApi.studentClassesCsv : reportsApi.studentClassesPdf;
      await fn(toListParams(effectiveFilter, 1, perPage));
    } catch (e: any) {
      setError(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(null);
    }
  };

  const gotoPage = (p: number) => {
    setPage(p);
    setTimeout(() => tableTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const goStudent = (id: number) => nav(`/student-history/${id}`);
  const goStaff   = (id: number) => nav(`/staff/staff-history/${id}`);
  // Canonical admin Class Detail route. `/check-staff/classes/list/:id` is
  // ClassDetailPage (Pages/Classes/CheckStaff/ClassDetailPage.tsx) — the same
  // page the "All Classes" list navigates to (Pages/Classes/List/checkList.tsx:985).
  // It calls GET /classes/{classId}/detail → ClassDetailController::show, which
  // queries `assign_classes::where('id', $classId)`. The report row's
  // `class_id` is exactly `assign_classes.id` (see StudentClassesReport.php:304),
  // so it maps 1-to-1 with no translation needed.
  //
  // Previous wrong destinations we tried:
  //   1. /class-list?class={id}      → 404 (route doesn't exist)
  //   2. /class-detail/{id}          → renders ParentClassDetail (parent portal,
  //                                    single-session), backend expects the
  //                                    legacy class_schedules.id, so
  //                                    assign_classes.id 1188 → "Class not found"
  const goClass   = (id: number) => nav(`/check-staff/classes/list/${id}`);

  return (
    <Box p={2}>
      <PremiumHeader
        refreshedAt={refreshedAt}
        loading={loadingList}
        exporting={exporting}
        onRefresh={loadList}
        onCsv={() => doExport("csv")}
        onPdf={() => doExport("pdf")}
      />

      <SummaryCards summary={summary} loading={loadingSummary && firstLoad} />

      <FilterPanel
        value={filter}
        onChange={setFilter}
        onClear={() => setFilter({ ...defaultFilter })}
        loading={loadingList || loadingOptions}
        options={options}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={loadList}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {/* Results card */}
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", position: "relative" }}>
        {/* Initial load — centred logo loader inside card */}
        {firstLoad && loadingList ? (
          <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <StepUpSpinner size="large" label="Loading student class assignments" />
          </Box>
        ) : (
          <>
            {/* Pagination / filter overlay while refreshing existing rows */}
            {loadingList && rows.length > 0 && (
              <Box sx={{
                position: "absolute", inset: 0, zIndex: 2,
                background: "rgba(255,255,255,0.6)", backdropFilter: "blur(1px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                pointerEvents: "none",
              }}>
                <StepUpSpinner size="medium" label="Loading page" />
              </Box>
            )}

            {isMobile ? (
              <Box p={1}>
                {rows.length === 0 && !loadingList && (
                  <EmptyState onClear={() => setFilter({ ...defaultFilter })} />
                )}
                {rows.map((r) => (
                  <MobileCard key={`${r.assignment_id}-${r.schedule_id}`} row={r}
                    onView={() => goStudent(r.student_id)} />
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 720 }}>
                <div ref={tableTopRef} />
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {["Student", "Class", "Grade & Subject", "Staff", "Schedule", "Mode & Location", "Assignment", "Status", ""]
                        .map((h) => (
                          <TableCell key={h}
                            sx={{ fontWeight: 700, background: "#f8fafc", color: "#1b3a6b",
                              textTransform: "uppercase", fontSize: 11, letterSpacing: 0.4 }}>
                            {h}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loadingList && rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9}>
                          <EmptyState onClear={() => setFilter({ ...defaultFilter })} />
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.map((r) => (
                      <ClassRow key={`${r.assignment_id}-${r.schedule_id}`}
                        row={r}
                        onViewStudent={goStudent}
                        onViewClass={goClass}
                        onViewStaff={goStaff}
                        onOpenDetails={setDetailsRow} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Footer / pagination */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center"
              sx={{ p: 1.5, borderTop: "1px solid #eef2f7", background: "#fbfcfe" }}>
              <Typography variant="body2" color="text.secondary" flex={1}>
                {meta && meta.total > 0
                  ? `Showing ${(meta.from ?? 0).toLocaleString()}–${(meta.to ?? 0).toLocaleString()} of ${meta.total.toLocaleString()} assignments`
                  : "—"}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Rows per page:</Typography>
                <Select size="small" value={perPage}
                  onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                  sx={{ minWidth: 80 }}>
                  {[25, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
                <Button size="small" disabled={!meta || page <= 1 || loadingList}
                  onClick={() => gotoPage(page - 1)}>Previous</Button>
                <Typography variant="body2">
                  {meta ? `${meta.current_page} / ${meta.last_page}` : "—"}
                </Typography>
                <Button size="small" disabled={!meta || page >= (meta?.last_page ?? 1) || loadingList}
                  onClick={() => gotoPage(page + 1)}>Next</Button>
              </Stack>
            </Stack>
          </>
        )}
      </Card>

      {/* Shared details modal — one entry point for the row title, "+N more"
          chips, and the actions menu. Uses the row data already in memory,
          so no extra API call is required to open it. */}
      <AssignmentDetailsModal
        row={detailsRow}
        open={!!detailsRow}
        onClose={() => setDetailsRow(null)}
        onViewStudent={goStudent}
        onViewClass={goClass}
        onViewStaff={goStaff}
      />
    </Box>
  );
}

function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Stack alignItems="center" spacing={1} py={5}>
      <Box sx={{ width: 56, height: 56 }}>
        <img src="/Images/stepup-logo.webp" alt="" style={{ width: "100%", opacity: 0.6 }} />
      </Box>
      <Typography fontWeight={600}>No class assignments found</Typography>
      <Typography variant="body2" color="text.secondary">
        Try changing or clearing the selected filters.
      </Typography>
      <Button variant="outlined" size="small" onClick={onClear}>Clear Filters</Button>
    </Stack>
  );
}

function MobileCard({ row, onView }: { row: Row; onView: () => void }) {
  const to12h = (t: string | null) => {
    if (!t) return "";
    const m = /^(\d{1,2}):(\d{2})/.exec(t); if (!m) return t;
    let h = Number(m[1]); const mm = m[2];
    const suffix = h >= 12 ? "PM" : "AM"; h = h % 12; if (h === 0) h = 12;
    return `${h}:${mm} ${suffix}`;
  };
  return (
    <Card variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
      <Typography fontWeight={600} onClick={onView}
        sx={{ color: "#1b3a6b", cursor: "pointer" }}>
        {row.student_name || "—"}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        ID {row.reference_id || row.student_id}{row.school_grade ? ` · ${row.school_grade}` : ""}
      </Typography>
      <Box mt={1}>
        <Typography variant="body2" fontWeight={600}>{row.class_name || `Class ${row.class_id}`}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.class_grades.join(", ")}{row.class_subjects.length ? ` · ${row.class_subjects.join(", ")}` : ""}
        </Typography>
      </Box>
      <Typography variant="body2" mt={1}>{row.staff_name || "—"}</Typography>
      <Typography variant="caption" color="text.secondary">
        {row.day || "—"} · {row.start_time ? `${to12h(row.start_time)}–${to12h(row.end_time)}` : "—"}
      </Typography>
      <Stack direction="row" spacing={1} mt={1} alignItems="center">
        <Chip size="small" variant="outlined"
          label={row.study_mode === "Offline" ? "In Person" : (row.study_mode || "—")} />
        <Typography variant="caption" color="text.secondary">{row.location || "Not assigned"}</Typography>
        <Box flex={1} />
        <Chip size="small" color="success" label={row.assignment_status === "Added" ? "Active" : (row.assignment_status || "—")} />
      </Stack>
    </Card>
  );
}
